import { jsPDF } from 'jspdf';

const MARGIN = 20;
const PAGE_WIDTH = 210;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const LINE_HEIGHT = 6;
const SECTION_GAP = 8;

const createReferralPDF = (formData) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = MARGIN;

  const checkPageBreak = (needed = 20) => {
    if (y + needed > 280) {
      doc.addPage();
      y = MARGIN;
    }
  };

  const addHeader = (text) => {
    checkPageBreak(16);
    doc.setFillColor(37, 99, 235);
    doc.rect(MARGIN, y, CONTENT_WIDTH, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(text, MARGIN + 3, y + 5.5);
    doc.setTextColor(0, 0, 0);
    y += 12;
  };

  const addSubHeader = (text) => {
    checkPageBreak(12);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(37, 99, 235);
    doc.text(text, MARGIN, y);
    doc.setTextColor(0, 0, 0);
    y += LINE_HEIGHT;
  };

  const addField = (label, value) => {
    if (!value && value !== 0) return;
    checkPageBreak(10);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(`${label}:`, MARGIN, y);
    doc.setFont('helvetica', 'normal');
    const labelWidth = doc.getTextWidth(`${label}: `);
    const maxWidth = CONTENT_WIDTH - labelWidth - 2;
    const lines = doc.splitTextToSize(String(value), maxWidth);
    doc.text(lines, MARGIN + labelWidth + 1, y);
    y += Math.max(LINE_HEIGHT, lines.length * 4.5);
  };

  const addFieldRow = (fields) => {
    checkPageBreak(10);
    const colWidth = CONTENT_WIDTH / fields.length;
    fields.forEach(([label, value], i) => {
      if (!value && value !== 0) return;
      const x = MARGIN + i * colWidth;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text(`${label}:`, x, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), x + doc.getTextWidth(`${label}: `) + 1, y);
    });
    y += LINE_HEIGHT;
  };

  const addLongText = (label, value) => {
    if (!value) return;
    checkPageBreak(14);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(`${label}:`, MARGIN, y);
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const lines = doc.splitTextToSize(String(value), CONTENT_WIDTH - 4);
    lines.forEach((line) => {
      checkPageBreak(6);
      doc.text(line, MARGIN + 2, y);
      y += 4;
    });
    y += 2;
  };

  const addSpacer = (h = 4) => { y += h; };

  // ═══════════════════════════════════════════
  //  TITLE
  // ═══════════════════════════════════════════
  doc.setFillColor(20, 50, 120);
  doc.rect(0, 0, PAGE_WIDTH, 28, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text('SDORA WELLBEING LLC', PAGE_WIDTH / 2, 10, { align: 'center' });
  doc.setFontSize(11);
  doc.text('OMHC & PRP Referral Form', PAGE_WIDTH / 2, 17, { align: 'center' });
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, PAGE_WIDTH / 2, 23, { align: 'center' });
  doc.setTextColor(0, 0, 0);
  y = 34;

  // ═══════════════════════════════════════════
  //  REFERRAL SOURCE
  // ═══════════════════════════════════════════
  addHeader('Referral Source Information');
  addFieldRow([['Referral Date', formData.referralDate], ['Urgency', formData.urgencyLevel]]);
  addFieldRow([['Source / Agency', formData.referralSource], ['Provider', formData.referringProvider]]);
  addFieldRow([['Credentials', formData.credentials], ['NPI', formData.npi]]);
  addFieldRow([['Phone', formData.phone], ['Email', formData.email]]);
  addFieldRow([['Contact Method', formData.contactMethod], ['Category', formData.referralCategory]]);
  addFieldRow([['Client Type', formData.clientType], ['Status', formData.referralStatus]]);
  addSpacer(SECTION_GAP);

  // ═══════════════════════════════════════════
  //  OMHC SECTION
  // ═══════════════════════════════════════════
  const omhc = formData.omhc || {};
  if (formData.referralCategory === 'OMHC' || formData.referralCategory === 'Both') {
    addHeader('OMHC Referral — Client Demographics');
    addFieldRow([['Last Name', omhc.lastName], ['First Name', omhc.firstName]]);
    addFieldRow([['Middle Name', omhc.middleName], ['Preferred Name', omhc.preferredName]]);
    addFieldRow([['Date of Birth', omhc.dob], ['Age', omhc.age]]);
    addFieldRow([['Sex Assigned', omhc.sexAssigned], ['Gender Identity', omhc.genderIdentity]]);
    addField('Pronouns', omhc.pronouns);
    addSpacer();

    addSubHeader('Insurance & Identification');
    addFieldRow([['SSN', omhc.ssn], ['Insurance Type', omhc.insuranceType]]);
    addSpacer();

    addSubHeader('Contact Information');
    addField('Address', [omhc.homeAddressLine1, omhc.homeAddressLine2].filter(Boolean).join(', '));
    addFieldRow([['City', omhc.homeCity], ['State', omhc.homeState]]);
    addFieldRow([['Zip', omhc.homeZip], ['Phone', omhc.clientPhone]]);
    addField('Email', omhc.clientEmail);
    addFieldRow([['Language', omhc.preferredLanguage], ['Interpreter', omhc.needInterpreter]]);
    if (omhc.mailingAddressDifferent) {
      addField('Mailing Address', [omhc.mailAddressLine1, omhc.mailAddressLine2, omhc.mailCity, omhc.mailState, omhc.mailZip].filter(Boolean).join(', '));
    }
    addSpacer();

    addSubHeader('Additional Demographics');
    addFieldRow([['Race', omhc.race], ['Ethnicity', omhc.ethnicity]]);
    addFieldRow([['Marital Status', omhc.maritalStatus], ['Living Situation', omhc.livingSituation]]);
    addField('Employment Status', omhc.employmentStatus);
    addSpacer();

    addSubHeader('Emergency Contact');
    addFieldRow([['Name', omhc.emergencyContactName], ['Relationship', omhc.emergencyRelationship]]);
    addField('Phone', omhc.emergencyPhone);
    addSpacer(SECTION_GAP);
  }

  // ═══════════════════════════════════════════
  //  PRP SECTION
  // ═══════════════════════════════════════════
  const prp = formData.prp || {};
  if (formData.referralCategory === 'PRP' || formData.referralCategory === 'Both') {
    addHeader('PRP Referral Information');
    addFieldRow([['Last Name', prp.lastName], ['First Name', prp.firstName]]);
    addFieldRow([['Middle Name', prp.middleName], ['Date of Birth', prp.dob]]);
    addFieldRow([['Age', prp.age], ['Referral Date', prp.referralDate]]);
    addFieldRow([['Current OMHC', prp.currentOMHC], ['Prior PRP', prp.priorPRP]]);
    if (prp.priorPRPProvider) addField('Prior PRP Provider', prp.priorPRPProvider);
    addSpacer();

    addSubHeader('Insurance & Identification');
    addFieldRow([['SSN', prp.ssn], ['Insurance Type', prp.insuranceType]]);
    addSpacer();

    addSubHeader('Contact Information');
    addField('Address', [prp.homeAddressLine1, prp.homeAddressLine2].filter(Boolean).join(', '));
    addFieldRow([['City', prp.homeCity], ['State', prp.homeState]]);
    addFieldRow([['Zip', prp.homeZip], ['Phone', prp.clientPhone]]);
    addSpacer();

    addSubHeader('Clinical Information');
    addField('Diagnosis', prp.currentDiagnosis);
    addField('Diagnosis Code', prp.currentDiagnosisCode);
    addLongText('Symptoms & Reason for Referral', prp.symptomsAndReason);
    addSpacer();

    // Functional Criteria
    const criteria = prp.functionalCriteria || {};
    const selectedKeys = Object.keys(criteria);
    if (selectedKeys.length > 0) {
      addSubHeader('Functional Criteria');
      addFieldRow([['New Onset Diagnosis', prp.newOnsetDiagnosis], ['Impairments 3+ Areas / 2yrs', prp.impairmentsOver2Years]]);
      addSpacer(2);
      const criteriaLabels = {
        employment: 'Inability to maintain competitive employment',
        dailyLiving: 'Inability to perform daily living activities',
        supportSystem: 'Inability to maintain personal support system',
        concentration: 'Deficiencies in concentration / persistence / pace',
        selfDirection: 'Deficiencies in self-direction',
        financialAssistance: 'Inability to procure financial assistance'
      };
      selectedKeys.forEach((key) => {
        const label = criteriaLabels[key] || key;
        addField(label, 'Selected');
        if (criteria[key].evidence) {
          addLongText('  Evidence', criteria[key].evidence);
        }
      });
      addSpacer();
    }

    addSubHeader('PRP Goals / Rehabilitation Needs');
    addLongText('Rehabilitation Goals', prp.rehabilitationGoals);
    addLongText('Strengths / Supports', prp.strengthsSupports);
    addLongText('Barriers to Treatment', prp.barriersToTreatment);
    addLongText('Additional Comments', prp.additionalComments);
    addSpacer();

    addSubHeader('Current Providers');
    addFieldRow([['PCP', prp.pcpName], ['PCP Phone', prp.pcpPhone]]);
    addFieldRow([['Therapist', prp.therapistName], ['Therapist Phone', prp.therapistPhone]]);
    addFieldRow([['Psychiatrist', prp.psychiatristName], ['Psychiatrist Phone', prp.psychiatristPhone]]);
    addField('Case Manager', prp.caseManager);
    addSpacer();

    // Supervision
    if (prp.clinicianUnderSupervision) {
      addSubHeader('Clinician Supervision');
      addField('Under Supervision', prp.clinicianUnderSupervision);
      if (prp.clinicianUnderSupervision === 'Yes') {
        addFieldRow([['Supervisor Credentials', prp.supervisorCredentials], ['Supervisor NPI', prp.supervisorNPI]]);
      }
    }
    addSpacer(SECTION_GAP);
  }

  // ═══════════════════════════════════════════
  //  SIGNATURES
  // ═══════════════════════════════════════════
  const sigs = formData.signatures || {};
  addHeader('Signatures & Authorization');
  const mode = sigs.signatureMode || 'typed';
  if (mode === 'typed' && sigs.typedSignature) {
    addField('Signature (Typed)', sigs.typedSignature);
  } else if (mode === 'draw' && sigs.signatureDataUrl) {
    checkPageBreak(30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('Signature (Drawn):', MARGIN, y);
    y += 3;
    try {
      doc.addImage(sigs.signatureDataUrl, 'PNG', MARGIN, y, 60, 20);
      y += 24;
    } catch {
      doc.setFont('helvetica', 'italic');
      doc.text('[Drawn signature attached]', MARGIN + 2, y + 4);
      y += 10;
    }
  }
  addField('Date', sigs.providerDate);
  addField('Authorization Agreed', sigs.authorizationAgreed ? 'Yes' : 'No');
  addSpacer(SECTION_GAP);

  // ═══════════════════════════════════════════
  //  ATTACHMENTS
  // ═══════════════════════════════════════════
  const attachments = formData.attachments || [];
  const uploadedFiles = formData.uploadedFiles || [];
  if (attachments.length > 0 || uploadedFiles.length > 0) {
    addHeader('Attached Documents');
    if (attachments.length > 0) {
      attachments.forEach((att) => {
        checkPageBreak(6);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(`  \u2022 ${att}`, MARGIN, y);
        y += 4.5;
      });
    }
    if (uploadedFiles.length > 0) {
      addSpacer(2);
      addSubHeader('Uploaded Files');
      uploadedFiles.forEach((file) => {
        checkPageBreak(6);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(`  \u2022 ${file.name}`, MARGIN, y);
        y += 4.5;
      });
    }
  }

  // ═══════════════════════════════════════════
  //  FOOTER on every page
  // ═══════════════════════════════════════════
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `SDORA WELLBEING LLC | Confidential Healthcare Document | Page ${i} of ${pageCount}`,
      PAGE_WIDTH / 2, 292, { align: 'center' }
    );
    doc.setTextColor(0, 0, 0);
  }

  return doc;
};

export const downloadReferralPDF = (formData) => {
  const doc = createReferralPDF(formData);
  const category = formData.referralCategory || 'Referral';
  const date = new Date().toISOString().split('T')[0];
  const lastName = formData.prp?.lastName || formData.omhc?.lastName || 'Client';
  doc.save(`SDORA_${category}_Referral_${lastName}_${date}.pdf`);
};
